// oxlint-disable unicorn/no-thenable
import { type UnwrapPromise } from "../../../types/internal/utility";

interface CancelledResultType {
    cancelled: boolean;
}

export class CancelledPromise extends Error {
    public cancelled = true;

    constructor(message: string, cancelled: boolean) {
        super(message);

        this.cancelled = cancelled;
    }
}

export class CancellablePromise<P, T = UnwrapPromise<P>> {
    private cancelled = false;
    private resolved = false;
    private cancelFn!: () => void;
    private wrappedPromise!: Promise<T>;

    private readonly onCancel?: boolean | (() => void);

    /**
     * Create a cancellable promise by wrapping an existing promise
     */
    constructor(promise: Promise<T>, onCancel?: boolean | (() => void)) {
        this.onCancel = onCancel;

        this.wrapPromise(promise);

        this.cancel = this.cancel.bind(this);
    }

    /**
     * Wrap a promise to be cancellable
     */
    private wrapPromise(promise: Promise<T>): void {
        this.wrappedPromise = new Promise((res, rej) => {
            this.cancelFn = (): void => {
                rej(new CancelledPromise("Promise Cancelled", this.cancelled));
            };

            promise.then(
                val => this.cancelled ? rej(new CancelledPromise("Promise Cancelled", this.cancelled)) : res(val),
                error => this.cancelled ? rej(new CancelledPromise("Promise Cancelled", this.cancelled)) : rej(error)
            )
                .then(() => {
                    this.resolved = true;
                });
        });
    }

    /**
     * Cancel the wrapped promise
     */
    public cancel(): void {
        if(this.cancelled || this.resolved) {
            return;
        }

        this.cancelled = true;

        if(typeof this.onCancel === "boolean" && this.onCancel) {
            this.cancelFn();
        } else if(typeof this.onCancel === "function") {
            this.onCancel();
            this.cancelFn();
        }
    }

    /**
     * Simulate `then` from a promise
     */
    public then<R, F = never>(
        onFulfilled: (result: T) => R | PromiseLike<R>,
        onRejected?: (error: unknown) => F | PromiseLike<F>
    ): Promise<R | F> {
        return this.wrappedPromise.then(onFulfilled, onRejected);
    }

    /**
     * Simulate `catch` from a promise
     */
    public catch<R>(callback: (result: unknown) => R | Promise<R>): Promise<T | R | CancelledResultType> {
        return this.wrappedPromise.catch(callback);
    }

    /**
     * Return the wrapped promise
     */
    public get promise(): Promise<T> {
        return this.wrappedPromise;
    }

    /**
     * Dynamically check if this has already been cancelled
     */
    public isCancelled(): boolean {
        return this.cancelled;
    }
}
